import React from 'react';
import { Alert } from '../../ui/alert';
import { Badge } from '../../ui/badge';
import { components } from 'react-select';
import { addDays, addYears } from 'date-fns';

const NoPermissions = () => {
  return (
    <Alert
      className="border border-border bg-destructive/10 text-destructive text-sm font-bold p-5"
      role="alert">
      You do not have permissions to access this page.
    </Alert>
  );
};

const BetaTag = ({ style }) => {
  return (
    <Badge
      className={`py-3 px-2 rounded-md h-[6px] w-[36px] text-[11px] font-bold items-center flex justify-center bg-white text-foreground border border-border ${style}`}>
      BETA
    </Badge>
  );
};

const CustomOption = (pageName) => (props) => {
  const { data } = props;
  return (
    <components.Option {...props}>
      <div id={`${pageName}-option-${data.label}`}>{data.label}</div>
    </components.Option>
  );
};

const WaveformLoader = () => {
  return (
    <div className='flex justify-between items-center w-24 h-20 p-2'>
      <div className='w-1.5 h-full bg-gray-900 animate-bar'></div>
      <div className='w-1.5 h-full bg-gray-900 animate-bar animation-delay-1'></div>
      <div className='w-1.5 h-full bg-gray-900 animate-bar animation-delay-2'></div>
      <div className='w-1.5 h-full bg-gray-900 animate-bar animation-delay-3'></div>
    </div>
  );
};

const AnnouncementAlert = ({ item }) => {
  function parseAnnouncementString(inputStr) {
    // Regex to match an <a> tag with its href attribute and inner text.
    // The "g" flag is required for global matching.
    const regex = /<a\s+href="([^"]+)"[^>]*>(.*?)<\/a>/gi;

    const fragments = [];
    let lastIndex = 0;
    let match;

    // Loop through all matches
    while ((match = regex.exec(inputStr)) !== null) {
      const matchStart = match.index;

      // Push the text before the <a> tag.
      if (matchStart > lastIndex) {
        fragments.push(inputStr.substring(lastIndex, matchStart));
      }

      // Push the link as a React element.
      const href = match[1];
      const linkText = match[2];
      fragments.push(
        <a
          key={lastIndex}
          href={href}
          target='_blank'
          rel='noopener noreferrer'
          style={{ color: item?.textColor, textDecoration: 'underline' }}>
          {linkText}
        </a>,
      );

      lastIndex = regex.lastIndex;
    }

    // Push any remaining text after the last <a> tag.
    if (lastIndex < inputStr?.length) {
      fragments.push(inputStr?.substring(lastIndex));
    }

    return <>{fragments}</>;
  }

  return (
    <Alert
      className="flex items-center rounded-lg border border-[#CCC]"
      style={{
        backgroundColor: item?.backgroundColor,
        justifyContent: item?.alignment,
      }}>
      <p
        className="text-sm"
        style={{ fontFamily: item?.font, color: item?.textColor, textAlign: item?.alignment }}>
        {parseAnnouncementString(item?.text)}
      </p>
    </Alert>
  );
};

// Helper function to calculate reporting year dates
const getReportingYearDates = (selectedYear, orgDetails) => {
  const isCalendarYearOrg = orgDetails?.reportingYearEndDay == 31 && orgDetails?.reportingYearEndMonth == 12;
  if (!orgDetails?.reportingYearEndDay || !orgDetails?.reportingYearEndMonth || isCalendarYearOrg) {
    // Fallback to calendar year if reporting year end is not set
    const fallbackFromDate = new Date(selectedYear, 0, 1);
    const fallbackToDate = new Date(selectedYear, 11, 31);
    return {
      fromDate: fallbackFromDate,
      toDate: fallbackToDate,
    };
  }

  const reportingYearEndMonth = orgDetails?.reportingMonth || orgDetails?.reportingYearEndMonth;
  const reportingYearEndDay = orgDetails?.reportingDay || orgDetails?.reportingYearEndDay;

  const toDate = new Date(selectedYear, reportingYearEndMonth - 1, reportingYearEndDay);
  const fromDate = addDays(new Date(selectedYear - 1, reportingYearEndMonth - 1, reportingYearEndDay), 1);

  return { fromDate, toDate };
};

// Helper function to calculate multi-year chart dates (8 years prior to current year)
const getMultiYearChartDates = (currentYear, orgDetails) => {
  if (!orgDetails?.reportingYearEndDay || !orgDetails?.reportingYearEndMonth) {
    // Fallback to calendar year if reporting year end is not set
    const fallbackStartDate = new Date(currentYear - 8, 0, 1);
    const fallbackEndDate = new Date(currentYear, 11, 31);
    return {
      startDate: fallbackStartDate,
      endDate: fallbackEndDate,
    };
  }

  const reportingYearEndMonth = orgDetails?.reportingMonth || orgDetails?.reportingYearEndMonth;
  const reportingYearEndDay = orgDetails?.reportingDay || orgDetails?.reportingYearEndDay;

  const currentDate = new Date();
  const currentYearReportingEnd = new Date(currentYear, reportingYearEndMonth - 1, reportingYearEndDay);
  const isCalendarYearOrg = orgDetails?.reportingYearEndDay == 31 && orgDetails?.reportingYearEndMonth == 12;

  // For multi-year charts, determine the effective year based on organization type
  let effectiveYear = currentYear;
  if (currentDate > currentYearReportingEnd && !isCalendarYearOrg) {
    effectiveYear = currentYear + 1;
  }

  // Calculate the end date (effective year's reporting year end)
  const endDate = new Date(effectiveYear, reportingYearEndMonth - 1, reportingYearEndDay);

  // Calculate the start date (8 years prior to current year's reporting year start)
  const startDate = addDays(addYears(endDate, -8), 1);

  return { startDate, endDate };
};

export {
  NoPermissions,
  BetaTag,
  CustomOption,
  WaveformLoader,
  AnnouncementAlert,
  getReportingYearDates,
  getMultiYearChartDates,
};
